import { isNil } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { TimeTrackerComponentData } from '@shared/data/entity/fields/TimeTrackerField';

//#region Public Interface

/**
 * A hook that returns the current time at a given interval.
 *
 * This is useful for components that need to update on a constant interval, like a timer.
 *
 * @param interval The interval in milliseconds.
 * @param isRunning Whether the timer is running. If false, the hook will not update the time.
 * @returns The current time of the timer.
 */
export function useTimer(interval: number, isRunning: boolean): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!isRunning) {
      return;
    }

    const timer = setInterval(() => setNow(new Date()), interval);
    return () => clearInterval(timer);
  }, [interval, isRunning]);

  return now;
}

/**
 * A function that starts a timer backed by time tracking data.
 *
 * @param data The time tracking data.
 * @param onDataChanged The function to call when the data changes.
 * @returns The function to start the timer.
 */
export function useOnStartTimeTracker(
  data: TimeTrackerComponentData,
  onDataChanged: (newData: TimeTrackerComponentData) => void,
) {
  return useCallback(() => {
    const newDatum = { start: { timestamp: new Date().toISOString() } };
    const newData = [...data, newDatum];
    onDataChanged(newData);
  }, [data, onDataChanged]);
}

/**
 * A function that stops a timer backed by time tracking data.
 *
 * @param data The time tracking data.
 * @param onDataChanged The function to call when the data changes.
 * @returns The function to stop the timer.
 */
export function useOnStopTimeTracker(
  data: TimeTrackerComponentData,
  onDataChanged: (newData: TimeTrackerComponentData) => void,
) {
  return useCallback(() => {
    for (let i = 0; i < data.length; i++) {
      if (isNil(data[i].end)) {
        const newData = [...data];
        newData[i] = {
          ...newData[i],
          end: { timestamp: new Date().toISOString() },
        };
        onDataChanged(newData);
        return;
      }
    }
  }, [data, onDataChanged]);
}

//#endregion
